import './TrevParCalculator.css'
import { Helmet } from 'react-helmet-async';
import ArrowRight from '../../icons/ArrowRight';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useLanguagePrefix from '../../LanguagePrefix';
import ToolFixedMenu from './ToolFixedMenu';

function TRevPARCalculator() {
    const langPrefix = useLanguagePrefix();
    const [totalRevenue, setTotalRevenue] = useState(null);
    const [roomAvailable, setRoomAvailable] = useState(null);
    const [TRevPAR, setTRevPAR] = useState(null);
    const { t } = useTranslation();


    const handleInputChange = (e, type) => {
        const value = e.target.value;
        if (type === 'revenue') {
            setTotalRevenue(value);
        } else if (type === 'available') {
            setRoomAvailable(value);
        }
    };


    const calculateTRevPAR = () => {
        if (totalRevenue > 0 && roomAvailable > 0) {
            const result = totalRevenue / roomAvailable;
            const formatted = Number.isInteger(result) ? result : result.toFixed(2);
            setTRevPAR(formatted);
        } else {
            setTRevPAR(null);
        }
    };

    return (
        <div>
            <Helmet>

                <title>TRevPAR Calculator – Total Revenue Per Available Room | Prostay</title>
                <meta name="description" content="Use Prostay’s free TRevPAR calculator to measure your hotel’s total revenue performance. Understand how much income each available room generates across all departments." />
                <link rel="canonical" href={"https://www.prostay.com" + langPrefix + "/trevpar-calculator/"} />

                 <link rel="alternate" href="https://www.prostay.com/es/trevpar-calculator/" />
                 <link rel="alternate" href="https://www.prostay.com/en/trevpar-calculator/" />

                <meta property="og:title" content="Occupancy Rate Calculator – Hotel Room Occupancy Formula | Prostay" />
                <meta property="og:description" content="Use Prostay’s free TRevPAR calculator to measure your hotel’s total revenue performance. Understand how much income each available room generates across all departments." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={"https://www.prostay.com" + langPrefix + "/trevpar-calculator/"} />
                <meta property="og:image" content='https://prostay.com/images/ogImage.png' />

                <meta property="og:site_name" content="Prostay" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Occupancy Rate Calculator – Hotel Room Occupancy Formula | Prostay" />
                <meta name="twitter:description" content="Use Prostay’s free TRevPAR calculator to measure your hotel’s total revenue performance. Understand how much income each available room generates across all departments." />
                <meta name="twitter:image" content='https://prostay.com/images/ogImage.png' />
                <meta name="twitter:site" content="@prostaypms" />
                <meta name="twitter:creator" content="@prostaypms" />


            </Helmet> 
            <div className='d-flex trevpar-calculator flex-column-reverse flex-xl-row'>
                <div className='d-flex flex-column width-left gap-4'>
                    <div>
                        <h1 style={{ margin: '0px' }}>
                            {t('TRevPAR Calculator')}
                        </h1>
                        <h1>
                            {t('Total Revenue Per Available Room')}
                        </h1>
                        <h6 className='mt-4'>
                            {t('Measure Total Hotel Revenue Per Room.')} <br />
                            {t('Use this free TRevPAR calculator to evaluate how much overall income each room in your property generates — not just from accommodation, but from every department.')}
                        </h6>
                        <div className='d-flex flex-column-reverse flex-xl-row gap-2 gap-sm-5 mt-5'>
                            <div className='d-flex flex-column gap-4'>
                                <div className='d-flex flex-column mt-4'>
                                    <label>
                                        {t('Total Revenue')}
                                    </label>
                                    <input
                                        type='number'
                                        value={totalRevenue}
                                        onChange={(e) => handleInputChange(e, 'revenue')}
                                        placeholder={t('Enter amount here')} />
                                </div>
                                <div className='d-flex flex-column mt-4'>
                                    <label>
                                        {t('Total Rooms Available')}
                                    </label>
                                    <input
                                        type='number'
                                        value={roomAvailable}
                                        onChange={(e) => handleInputChange(e, 'available')}
                                        placeholder={t('Enter number here')} />
                                </div>
                                <div className='d-flex gap-3 gap-sm-5 flex-column flex-sm-row align-items-center'>
                                    <button className='light-blue-filled-btn-xsmall' style={{ height: '38px', width: '111px' }} onClick={calculateTRevPAR}>
                                        {t('Calculate')}
                                        <ArrowRight />
                                    </button>
                                    <div className='d-flex flex-column w-100'>
                                        <label>
                                            {t('TRevPAR')}
                                        </label>
                                        <input
                                            readOnly
                                            value={TRevPAR}
                                            type='number'
                                            placeholder='0' />
                                    </div>
                                </div>
                            </div>
                            <div className='center-div'>
                                <h2>
                                    {t('What is TRevPAR?')}
                                </h2>
                                <p className='mt-3'>
                                    {t('TRevPAR, or Total Revenue Per Available Room, is a hotel performance metric that measures the total revenue generated across the entire property, divided by the number of available rooms.')}
                                </p>
                                <br />
                                <p>
                                    {t('Unlike ADR or RevPAR, which focus only on room sales, TRevPAR includes all revenue sources — such as food and beverage, spa, retail, tours, or any other ancillary services. This makes TRevPAR a more complete indicator of overall property performance. It is especially useful for full-service hotels and resorts where non-room revenue plays a large role in the business model.')}
                                </p>
                                <br />
                                <p>
                                    {t('By understanding how much total income each room is contributing, hotel managers can evaluate the true productivity of their inventory and make decisions that drive property-wide revenue growth.')}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className='gap-3 d-flex flex-column mt-3'>
                        <h2>
                            {t('TRevPAR Formula')}
                        </h2>
                        <div>
                            <p>
                                {t('To calculate TRevPAR, you divide your property’s total revenue by the total number of available rooms during a specific period.')}
                            </p>
                            <p>
                                {t('The formula looks like this:')}
                            </p>
                        </div>
                        <div className='d-flex align-items-center formula gap-2'>
                            <p>
                                {t('TRevPAR')}  =
                            </p>
                            <div className='d-flex flex-column align-items-center'>
                                <p>
                                    {t('Total Revenue')}
                                </p>
                                <p style={{ borderTop: '0.5px solid #1643E0' }}>
                                    {t('Total Available Rooms')}
                                </p>
                            </div>
                        </div>
                        <p>
                            {t('Total revenue includes all income generated during the period — not just from rooms, but from all operating departments. For example, if your hotel brings in $120,000 in total revenue over 30 days and has 100 rooms available, you divide $120,000 by 3,000 available room nights (100 rooms × 30 days). That results in a TRevPAR of $40. This means that, on average, each room is generating $40 in revenue every day, even if it’s not sold.')}
                        </p>
                    </div>
                    <div className='d-flex flex-column gap-3'>
                        <h2>
                            {t('Why TRevPAR is Important for Full-Service Hotels')}
                        </h2>
                        <p>
                            {t('TRevPAR is especially valuable for hotels and resorts that generate significant revenue from non-room services. In these cases, focusing only on ADR or RevPAR can give an incomplete view of performance.')}
                        </p>
                        <p>
                            {t('TRevPAR brings everything together — rooms, restaurants, spa, bar, retail, activities — to show the true earning power of the property. By tracking this metric, hotel managers and revenue teams can identify high-performing departments, uncover revenue leaks, and spot new opportunities for upselling or cross-selling.')}
                        </p>
                        <p>
                            {t('It also helps with strategic decisions around investment, marketing, and resource allocation, because it shows how each part of the guest experience contributes to overall success.')}
                        </p>
                    </div>
                    <div className='d-flex flex-column gap-3 mt-2'>
                        <h2>
                            {t('How TRevPAR Works with RevPAR and GOPPAR')}
                        </h2>
                        <p>
                            {t('TRevPAR focuses on total revenue generation, while RevPAR focuses on room revenue, and GOPPAR focuses on profit. Each metric serves a specific purpose, and when used together, they provide a full picture of hotel performance.')}
                        </p>
                        <p>
                            {t('For example, a high TRevPAR alongside a modest RevPAR might indicate that your food and beverage or spa offerings are performing very well. On the other hand, a strong TRevPAR with a weak GOPPAR could reveal that operational expenses are cutting into profitability.')}
                        </p>
                        <p>
                            {t('Using all three metrics allows you to balance top-line growth with cost efficiency and ensure that every department is contributing in a profitable way.')}
                        </p>
                    </div>
                    <div className='d-flex flex-column gap-3 mt-2'>
                        <h2>
                            {t('Grow Revenue Across Every Department with Prostay')}
                        </h2>
                        <p>
                            {t('Prostay helps hoteliers move beyond room-focused metrics by giving you real-time visibility into total property performance.')}
                        </p>
                        <p>
                            {t('With TRevPAR integrated directly into your dashboards, you can monitor revenue from each department, run comparative reports, and track how ancillary sales impact your bottom line.')}
                        </p>
                        <p>
                            {t('From boutique properties to large resorts, Prostay gives you the data and automation tools to grow revenue in every corner of your business.')}
                        </p>
                    </div>
                </div>
                <ToolFixedMenu />
            </div>
        </div>
    )
}

export default TRevPARCalculator;